/**
 * Stage select screen input — grid navigation, random preview, confirm.
 */

import { Keys } from 'bonkjs';
import type { InputProvider } from '../../input';
import type { GameState } from '../../types';
import type { GameEffect } from '../effects';
import { transitionPhase } from '../effects';
import { resetGame } from '../reset';
import { TURN_FLOW } from '../../constants';
import { justPressed, msToFrames } from '../input-edge';
import { getAllStageIds } from '../../stages';
import { setActiveLevel } from '../level-loader';
import { pickRandomThemeId, makePreviewSeed } from './helpers';
import { playSound } from '../../audio';

export function handleStageSelectInput(state: GameState, input: InputProvider, fx: GameEffect[]): void {
  // Input gate — prevents Space/Enter leak from tank select confirm (especially touch tapVirtualKey)
  if (state.frameCount < state.freezeUntil) return;

  const isMP = !!state.multiplayer?.enabled;

  // Multiplayer: once stageSelectReady, transition to mp_waiting
  if (isMP && state.stageSelectReady) {
    state.stageSelectReady = false; // consume flag — prevent re-firing
    fx.push({ type: 'phase_transition', from: 'stage_select', to: 'mp_waiting' });
    return;
  }

  // Multiplayer: only the chooser can navigate/confirm
  if (isMP && state.multiplayer!.mySlot !== state.multiplayer!.stageSelectChooser) {
    return; // Non-chooser waits
  }

  const stageIds = getAllStageIds();
  const cols = 4;
  const total = stageIds.length;
  const rows = Math.ceil(total / cols);

  let col = state.stageSelectCursor % cols;
  let row = Math.floor(state.stageSelectCursor / cols);

  // Navigate grid (both P1 and P2 controls)
  if (justPressed(Keys.A, input) || justPressed(Keys.ArrowLeft, input)) {
    col = (col - 1 + cols) % cols;
  }
  if (justPressed(Keys.D, input) || justPressed(Keys.ArrowRight, input)) {
    col = (col + 1) % cols;
  }
  if (justPressed(Keys.W, input) || justPressed(Keys.ArrowUp, input)) {
    row = (row - 1 + rows) % rows;
  }
  if (justPressed(Keys.S, input) || justPressed(Keys.ArrowDown, input)) {
    row = (row + 1) % rows;
  }

  // Clamp to valid cells (skip empty slots in partial row)
  let newIdx = row * cols + col;
  if (newIdx >= total) {
    // Wrap column to last valid cell in this row
    col = Math.min(col, total - 1 - row * cols);
    if (col < 0) { row = 0; col = 0; }
    newIdx = row * cols + col;
  }
  if (newIdx < total && newIdx !== state.stageSelectCursor) {
    state.stageSelectCursor = newIdx;
    playSound('menu_nav');
  } else if (newIdx < total) {
    state.stageSelectCursor = newIdx;
  }

  // Reroll random preview (R key when cursor is on the random cell)
  const selectedId = stageIds[state.stageSelectCursor];
  if (selectedId === 'random' && justPressed(Keys.R, input)) {
    state.randomPreviewSeed = makePreviewSeed();
    state.randomPreviewThemeId = pickRandomThemeId();
  }

  // Confirm
  if (justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) {
    playSound('menu_select');
    // Clear any custom level from ?level= URL param so resetGame uses stage pipeline
    setActiveLevel(null);

    if (isMP) {
      // Multiplayer — set stageId + emit effect for network sync, don't resetGame yet
      if (selectedId === 'random') {
        state.stageId = '';
        fx.push({ type: 'stage_theme_change', stageId: state.randomPreviewThemeId });
        fx.push({ type: 'stage_select_confirmed', stageId: '', seed: state.randomPreviewSeed, themeId: state.randomPreviewThemeId });
      } else {
        state.stageId = selectedId;
        fx.push({ type: 'stage_theme_change', stageId: selectedId });
        fx.push({ type: 'stage_select_confirmed', stageId: selectedId, seed: state.randomPreviewSeed });
      }
      state.stageSelectReady = true;
    } else {
      // Local — immediate resetGame
      if (selectedId === 'random') {
        state.stageId = '';
        state.roundNumber = 0;
        fx.push({ type: 'stage_theme_change', stageId: state.randomPreviewThemeId });
        fx.push(...resetGame(state, state.randomPreviewSeed));
      } else {
        state.stageId = selectedId;
        state.roundNumber = 0;
        fx.push({ type: 'stage_theme_change', stageId: selectedId });
        fx.push(...resetGame(state));
      }
      state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
    }
  }

  // Back to tank select (disabled in multiplayer)
  if (!isMP && justPressed(Keys.Escape, input)) {
    state.tankSelectReady = [false, false];
    transitionPhase(state, 'tank_select', fx);
  }
}
